import { resolveBenchSlats } from "./benchSlats";
import { resolveBenchSkirt } from "./benchSkirt";
import { resolveBenchBackrest } from "./benchBackrest";
/**
 * Interior layout validation.
 *
 * Everything the user can now move — bench runs and the heater — is checked
 * against the rules that used to be enforced implicitly by the engines
 * generating the layout themselves.
 *
 * Two deliberate choices:
 *
 * 1. **No duplicated thresholds.** The heater clearances come from the same
 *    `heater.clearance.*` rules `calculateHeaterClearance` reads, and the lav
 *    heights come from `calculateBenchErgonomics`, not from a second copy of
 *    Löylyns lag. Everything tunable resolves through `getRuleValue`.
 * 2. **Validated geometry is BUILT geometry.** Runs are clipped around the
 *    heater first (see `effectiveRuns.ts`), so validation never reports a
 *    collision the renderer already resolves by shortening the bench.
 */
import { benchAssembliesOverlap, occupiedFloorAreaSqm } from "./benchCollision";
import { benchSupportFits } from "./benchSupport";
import { benchSeatTopMm, benchSeatHeightBoundsMm } from "./benchHeight";
import { getRuleValue, type RuleOverrides } from "../../data/ruleRegistry";
import type { HeaterType } from "../../engines/types";
import { calculateBenchErgonomics } from "../ergonomics/calculateBenchErgonomics";
import { resolveHeaterClearanceMm } from "../heater/calculateHeaterClearance";
import type { Market } from "../location/types";

import { resolveEffectiveLayout } from "./effectiveRuns";
import {
	type FootprintMm,
	footprintFromWallSpans,
	footprintsOverlap,
	type HeaterSizeMm,
	type InteriorLayout,
	type InteriorRoom,
	type InteriorWall,
	roomFloorAreaSqm,
	wallAxisOriginMm,
	wallFaceMm,
	wallInwardSign,
} from "./types";

// ─── Types ──────────────────────────────────────────────────

export type InteriorIssueCode =
	| "heater-clearance"
	| "door-swing"
	| "door-entry"
	| "free-floor"
	| "bench-depth"
	| "bench-overlap"
	| "loyly-height"
	| "bench-step-height"
	| "bench-height"
	| "bench-headroom"
	| "bench-support"
	| "bench-backrest"
	| "bench-front"
	| "bench-slats";

export type InteriorIssueMessageKey =
	| "domain_interior_issue_heaterClearance"
	| "domain_interior_issue_doorSwing"
	| "domain_interior_issue_doorEntry"
	| "domain_interior_issue_freeFloor"
	| "domain_interior_issue_benchDepth"
	| "domain_interior_issue_benchOverlap"
	| "domain_interior_issue_loylyHeight"
	| "domain_interior_issue_benchStepHeight"
	| "domain_interior_issue_benchHeight"
	| "domain_interior_issue_benchHeadroom"
	| "domain_interior_issue_benchSupport"
	| "domain_interior_issue_benchBackrest"
	| "domain_interior_issue_benchFront"
	| "domain_interior_issue_benchSlats";

const MESSAGE_KEY_BY_CODE: Record<InteriorIssueCode, InteriorIssueMessageKey> =
	{
		"heater-clearance": "domain_interior_issue_heaterClearance",
		"door-swing": "domain_interior_issue_doorSwing",
		"door-entry": "domain_interior_issue_doorEntry",
		"free-floor": "domain_interior_issue_freeFloor",
		"bench-depth": "domain_interior_issue_benchDepth",
		"bench-overlap": "domain_interior_issue_benchOverlap",
		"loyly-height": "domain_interior_issue_loylyHeight",
		"bench-step-height": "domain_interior_issue_benchStepHeight",
		"bench-height": "domain_interior_issue_benchHeight",
		"bench-headroom": "domain_interior_issue_benchHeadroom",
		"bench-support": "domain_interior_issue_benchSupport",
		"bench-backrest": "domain_interior_issue_benchBackrest",
		"bench-front": "domain_interior_issue_benchFront",
		"bench-slats": "domain_interior_issue_benchSlats",
	};

export interface InteriorLayoutIssue {
	code: InteriorIssueCode;
	severity: "error" | "warning";
	/** Resolve at the render boundary: `t[messageKey]` with `args` substituted. */
	messageKey: InteriorIssueMessageKey;
	args: Record<string, string | number>;
	/** Bench run the issue concerns, when it is about one specific run. */
	runId?: string;
}

export interface InteriorLayoutValidationResult {
	/** False when at least one `error` issue is present. */
	valid: boolean;
	issues: InteriorLayoutIssue[];
}

/**
 * A door the layout has to leave room for, expressed in the same wall-relative
 * coordinates as a `BenchRun`: `startMm` runs along the wall from its
 * low-coordinate corner.
 */
export interface InteriorDoor {
	id: string;
	wall: InteriorWall;
	startMm: number;
	widthMm: number;
	/**
	 * Whether the leaf sweeps into THIS room (issue 172). Absent = true, the
	 * old assumption. A sauna door opens outward, so it sweeps the outside or
	 * the relax room, and the sauna only needs room to step in.
	 */
	swingsIntoRoom?: boolean;
}

export interface ValidateInteriorLayoutInput {
	layout: InteriorLayout;
	room: InteriorRoom;
	heaterType: HeaterType;
	/** Chosen catalog model; its datasheet distances bind the lavar (issue 159). */
	heaterModelId?: string | null;
	/**
	 * Omit for a room with no heater — the relax side. Runs there are then never
	 * clipped and no clearance issue can be raised against them.
	 */
	heaterSize?: HeaterSizeMm;
	doors?: InteriorDoor[];
	/** Interior ceiling height, for the Löyly check. */
	ceilingHeightMm?: number;
}

// ─── Internals ──────────────────────────────────────────────

function issue(
	code: InteriorIssueCode,
	severity: InteriorLayoutIssue["severity"],
	args: Record<string, string | number>,
	runId?: string,
): InteriorLayoutIssue {
	return { code, severity, messageKey: MESSAGE_KEY_BY_CODE[code], args, runId };
}

/**
 * Swing footprint of a door: the quarter-circle a leaf sweeps, approximated as
 * the rectangle in front of the opening. Same wall convention as a bench run.
 */
export function doorSwingBox(
	door: InteriorDoor,
	room: InteriorRoom,
	swingDepthMm: number,
): FootprintMm {
	const origin = wallAxisOriginMm(room, door.wall);
	const near = wallFaceMm(room, door.wall);
	const far = near + wallInwardSign(door.wall) * swingDepthMm;
	return footprintFromWallSpans(
		door.wall,
		{ min: origin + door.startMm, max: origin + door.startMm + door.widthMm },
		{ min: Math.min(near, far), max: Math.max(near, far) },
	);
}

/**
 * Breach of the heater's directional clearances, or null when clear.
 *
 * `sideMm` governs X and `frontMm` governs Z — the same split
 * `calculateHeaterClearance` uses. Which rule applies is decided by which axis
 * actually SEPARATES the two boxes: two things side by side are held apart by
 * the X gap, and their Z overlap says nothing about how close they are.
 * Collapsing both into one `Math.min` threshold accepted a bench 130 mm to the
 * side of a wood heater, which needs 150.
 */
function axisClearanceBreach(
	bench: FootprintMm,
	heater: FootprintMm,
	sideMm: number,
	frontMm: number,
): { requiredMm: number; actualMm: number } | null {
	const gapX = Math.max(
		bench.minXMm - heater.maxXMm,
		heater.minXMm - bench.maxXMm,
	);
	const gapZ = Math.max(
		bench.minZMm - heater.maxZMm,
		heater.minZMm - bench.maxZMm,
	);

	// Clear as soon as ONE separating axis satisfies its own rule.
	if (gapX >= sideMm || gapZ >= frontMm) return null;

	// Otherwise report whichever axis is doing the separating. When neither is
	// (the boxes intersect in plan) the shallower penetration is the honest
	// number, against the tighter of the two rules.
	if (gapX >= 0 && gapZ < 0) {
		return { requiredMm: sideMm, actualMm: Math.round(gapX) };
	}
	if (gapZ >= 0 && gapX < 0) {
		return { requiredMm: frontMm, actualMm: Math.round(gapZ) };
	}
	if (gapX >= 0 && gapZ >= 0) {
		// Diagonal: neither gap reaches its rule, report the nearer shortfall.
		return gapX - sideMm >= gapZ - frontMm
			? { requiredMm: sideMm, actualMm: Math.round(gapX) }
			: { requiredMm: frontMm, actualMm: Math.round(gapZ) };
	}
	return {
		requiredMm: Math.min(sideMm, frontMm),
		actualMm: Math.round(Math.max(gapX, gapZ)),
	};
}

// ─── Public API ─────────────────────────────────────────────

export function validateInteriorLayout(
	input: ValidateInteriorLayoutInput,
	overrides: RuleOverrides = {},
	market: Market = "se",
): InteriorLayoutValidationResult {
	const issues: InteriorLayoutIssue[] = [];
	const { layout, room, heaterSize } = input;
	const wallHeightMm =
		room.wallHeightMm ??
		getRuleValue<number>("dimensions.wallHeightM", overrides) * 1000;
	const bounds = benchSeatHeightBoundsMm(wallHeightMm);
	for (const run of layout.benchRuns.filter((run) => run.room === room.id)) {
		if (
			run.seatHeightMm !== undefined &&
			(!Number.isFinite(run.seatHeightMm) ||
				run.seatHeightMm < bounds.minMm ||
				run.seatHeightMm > bounds.maxMm)
		) {
			issues.push(issue("bench-height", "error", bounds, run.id));
		}
		if (run.seatHeightMm !== undefined && run.tier === "upper") {
			const requiredMm = getRuleValue<number>(
				"ergonomics.upperBenchOffsetMm",
				overrides,
			);
			const actualMm = wallHeightMm - run.seatHeightMm;
			if (actualMm < requiredMm)
				issues.push(
					issue("bench-headroom", "warning", { actualMm, requiredMm }, run.id),
				);
		}
	}

	const { runs, dropped, heaterBox } = resolveEffectiveLayout(
		layout,
		room,
		heaterSize,
	);

	for (const { effective } of runs) {
		if (effective.slatGapMm !== undefined && (!Number.isFinite(effective.slatGapMm) || effective.slatGapMm < 6 || effective.slatGapMm > 25 || resolveBenchSlats(effective).widthMm < 45))
			issues.push(issue("bench-slats", "error", {}, effective.id));
		if (effective.skirtEnabled === true && !resolveBenchSkirt(effective, room))
			issues.push(issue("bench-front", "error", {}, effective.id));
		if (effective.backrestEnabled && !resolveBenchBackrest(effective, room))
			issues.push(issue("bench-backrest", "error", {}, effective.id));
		if (!benchSupportFits(effective, room))
			issues.push(issue("bench-support", "error", {}, effective.id));
	}

	// 1. Heater clearance — the SAME rules `calculateHeaterClearance` reads, so
	//    an admin override moves both checks at once.
	//
	//    Severity is split deliberately. The renderer already shortens a run
	//    that meets the heater, so a *surviving* run being tighter than the rule
	//    is advice, and the authoritative check stays the 3D one in
	//    `calculateHeaterClearance` (which knows the bench sits above the
	//    stones, where a plan-view test cannot). A run the heater left no usable
	//    span for is the genuine failure: the user asked for a bench that the
	//    model will silently not build.
	const { sideMm, frontMm } = resolveHeaterClearanceMm(input, overrides);
	for (const run of dropped) {
		issues.push(
			issue(
				"heater-clearance",
				"error",
				{ requiredMm: Math.min(sideMm, frontMm), actualMm: 0 },
				run.id,
			),
		);
	}

	if (heaterBox) {
		for (const { effective, footprint, clipped } of runs) {
			// A clipped run sits at the clip clearance BY CONSTRUCTION — the
			// renderer put it there. Warning about it would fire on every design
			// whose bench band crosses the heater (every two-room with a relax
			// room, for one) and mean nothing. The 3D check in
			// `calculateHeaterClearance` still sees the built bench.
			if (clipped) continue;
			// Per AXIS, against its own rule. Collapsing the two with `Math.min`
			// accepted a bench 130 mm to the SIDE of a wood heater, which needs
			// 150 — `calculateHeaterClearance` compares them separately and would
			// have rejected it.
			const breach = axisClearanceBreach(footprint, heaterBox, sideMm, frontMm);
			if (breach) {
				issues.push(issue("heater-clearance", "warning", breach, effective.id));
			}
		}
	}

	// 2. Door swing — a leaf must not open into a lav. A leaf that opens away
	//    from this room cannot hit one; a lav in front of it only makes the
	//    step in awkward, so it is a warning over a shallower zone.
	const swingDepthMm = getRuleValue<number>(
		"ergonomics.doorSwingClearanceMm",
		overrides,
	);
	const entryDepthMm = getRuleValue<number>(
		"ergonomics.doorEntryClearanceMm",
		overrides,
	);
	for (const door of input.doors ?? []) {
		const intoRoom = door.swingsIntoRoom !== false;
		const zone = doorSwingBox(
			door,
			room,
			intoRoom ? swingDepthMm : entryDepthMm,
		);
		for (const { effective, footprint } of runs) {
			if (!footprintsOverlap(footprint, zone)) continue;
			issues.push(
				intoRoom
					? issue(
							"door-swing",
							"error",
							{ swingDepthMm, doorId: door.id },
							effective.id,
						)
					: issue(
							"door-entry",
							"warning",
							{ entryDepthMm, doorId: door.id },
							effective.id,
						),
			);
		}
	}

	// 3. Minimum free floor: what is left once benches and heater are placed.
	const minFreeFloorSqm = getRuleValue<number>(
		"ergonomics.minFreeFloorAreaSqm",
		overrides,
	);
	const occupiedSqm = occupiedFloorAreaSqm([
		...runs.map((entry) => entry.footprint),
		...(heaterBox ? [heaterBox] : []),
	]);
	const freeFloorSqm = roomFloorAreaSqm(room) - occupiedSqm;
	if (freeFloorSqm < minFreeFloorSqm) {
		issues.push(
			issue("free-floor", "error", {
				requiredSqm: minFreeFloorSqm,
				actualSqm: Number(Math.max(0, freeFloorSqm).toFixed(2)),
			}),
		);
	}

	// 4. Bench depth against a usable seat.
	const minSeatDepthMm = getRuleValue<number>(
		"ergonomics.benchDepthMinMm",
		overrides,
	);
	const lyingDepthMm = getRuleValue<number>(
		"ergonomics.lyingDepthMinMm",
		overrides,
	);
	// Depth rules are BATHING rules: how deep a lav must be to sit or lie on in
	// the hot room. The relax bench is ordinary furniture at 380 mm, and judging
	// it by `benchDepthMinMm` (400) made every stock two-room-with-relax design
	// report itself invalid. Same reasoning as the Löyly gate below.
	for (const { effective } of room.id === "sauna" ? runs : []) {
		if (effective.depthMm < minSeatDepthMm) {
			issues.push(
				issue(
					"bench-depth",
					"error",
					{ requiredMm: minSeatDepthMm, actualMm: effective.depthMm },
					effective.id,
				),
			);
		} else if (effective.tier === "upper" && effective.depthMm < lyingDepthMm) {
			// Advisory: you can sit, you cannot lie down.
			issues.push(
				issue(
					"bench-depth",
					"warning",
					{ requiredMm: lyingDepthMm, actualMm: effective.depthMm },
					effective.id,
				),
			);
		}
	}

	// 5. Overlapping projections are valid only when the assemblies clear in height.
	for (let i = 0; i < runs.length; i += 1) {
		for (let j = i + 1; j < runs.length; j += 1) {
			if (benchAssembliesOverlap(runs[i].effective, runs[j].effective, room)) {
				issues.push(
					issue(
						"bench-overlap",
						"error",
						{ otherRunId: runs[j].effective.id },
						runs[i].effective.id,
					),
				);
			}
		}
	}

	// 6. Löylyns lag — reuse the ergonomics domain rather than re-deriving it.
	const ceilingHeightMm =
		input.ceilingHeightMm ??
		room.wallHeightMm ??
		getRuleValue<number>("dimensions.wallHeightM", overrides) * 1000;
	const ergonomics = calculateBenchErgonomics(
		{ ceilingHeightMm },
		overrides,
		market,
	);
	// Löylyns lag governs the HOT room's bench heights; a relax bench is
	// furniture, not a bathing position, so the rule does not apply to it.
	if (room.id === "sauna" && !ergonomics.isLoylyCompliant && runs.length > 0) {
		issues.push(
			issue("loyly-height", "warning", {
				minCeilingMm: ergonomics.minCeilingMm,
				actualCeilingMm: ceilingHeightMm,
			}),
		);
	}

	// 7. The other end of the same law (issue 155): the lavar hang from the
	//    wall top, so a taller wall lifts the foot lav out of reach of the
	//    floor. Measured on the seat TOP the model draws, not on the ergonomics
	//    model's idealised offsets. Only the foot lav: a single-tier layout
	//    ("straight") is an upper lav with no step by design, already 1.2 m up
	//    at the default wall, and is not what a wall-height change breaks.
	const footRuns = runs.filter(({ effective }) => effective.tier === "lower");
	if (room.id === "sauna" && footRuns.length > 0) {
		const wallHeightMm =
			room.wallHeightMm ??
			getRuleValue<number>("dimensions.wallHeightM", overrides) * 1000;
		const lowestSeatTopMm = Math.min(
			...footRuns.map(({ effective }) =>
				benchSeatTopMm(effective, wallHeightMm),
			),
		);
		const maxMm = getRuleValue<number>(
			"ergonomics.maxLowestBenchFromFloorMm",
			overrides,
		);
		if (lowestSeatTopMm > maxMm) {
			issues.push(
				issue("bench-step-height", "warning", {
					actualMm: Math.round(lowestSeatTopMm),
					maxMm,
				}),
			);
		}
	}

	return {
		valid: issues.every((entry) => entry.severity !== "error"),
		issues,
	};
}
